import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "../../components/ui/use-toast";
import { methodologyService } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminSettings = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const { settings, refreshSettings } = useSystemSettings();

  // Settings state
  const [disableLogin, setDisableLogin] = useState(settings.disableLogin || false);
  const [disableSignup, setDisableSignup] = useState(settings.disableSignup || false);
  const [disableAI, setDisableAI] = useState(settings.disableAI || false);
  const [disableStraightUpMethodologies, setDisableStraightUpMethodologies] = useState(settings.disableStraightUpMethodologies || false);
  const [disableGuidedMethodologies, setDisableGuidedMethodologies] = useState(settings.disableGuidedMethodologies || false);
  const [disablePathway, setDisablePathway] = useState(settings.disablePathway || false);
  const [disablePentestOrchestrator, setDisablePentestOrchestrator] = useState(settings.disablePentestOrchestrator || false);
  const [disableHuntersBoard, setDisableHuntersBoard] = useState(settings.disableHuntersBoard || false);
  const [disableHackerChecklists, setDisableHackerChecklists] = useState(settings.disableHackerChecklists || false);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  // Update local state when settings change
  useEffect(() => {
    setDisableLogin(settings.disableLogin || false);
    setDisableSignup(settings.disableSignup || false);
    setDisableAI(settings.disableAI || false);
    setDisableStraightUpMethodologies(settings.disableStraightUpMethodologies || false);
    setDisableGuidedMethodologies(settings.disableGuidedMethodologies || false);
    setDisablePathway(settings.disablePathway || false);
    setDisablePentestOrchestrator(settings.disablePentestOrchestrator || false);
    setDisableHuntersBoard(settings.disableHuntersBoard || false);
    setDisableHackerChecklists(settings.disableHackerChecklists || false);
  }, [settings]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/aAyuiOcvMq');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      await methodologyService.updateSystemSettings({
        ...settings,
        disableLogin,
        disableSignup,
        disableAI,
        disableStraightUpMethodologies,
        disableGuidedMethodologies,
        disablePathway,
        disablePentestOrchestrator,
        disableHuntersBoard,
        disableHackerChecklists
      });
      
      // Refresh the settings in the context
      await refreshSettings();
      
      toast({
        title: "Settings saved",
        description: "Your system settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/aAyuiOcvMq/admin')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-8">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              System Settings
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Configure global system settings and feature availability
            </p>
          </div>

          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Authentication Settings</CardTitle>
                <CardDescription>Control user access to the platform</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-login" className="font-medium">Disable Login</Label>
                    <p className="text-sm text-gray-500">Prevents users from logging into the platform</p>
                  </div>
                  <Switch 
                    id="disable-login" 
                    checked={disableLogin}
                    onCheckedChange={(checked) => setDisableLogin(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-signup" className="font-medium">Disable Signup</Label>
                    <p className="text-sm text-gray-500">Prevents new users from creating accounts</p>
                  </div>
                  <Switch 
                    id="disable-signup" 
                    checked={disableSignup}
                    onCheckedChange={(checked) => setDisableSignup(checked)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Feature Settings</CardTitle>
                <CardDescription>Control which features are available to users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-ai" className="font-medium">Disable AI Features</Label>
                    <p className="text-sm text-gray-500">Turns off all AI-related functionality</p>
                  </div>
                  <Switch 
                    id="disable-ai" 
                    checked={disableAI}
                    onCheckedChange={(checked) => setDisableAI(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-straight-up" className="font-medium">Disable Straight-up Methodologies</Label>
                    <p className="text-sm text-gray-500">Hides all straight-up methodology content</p>
                  </div>
                  <Switch 
                    id="disable-straight-up" 
                    checked={disableStraightUpMethodologies}
                    onCheckedChange={(checked) => setDisableStraightUpMethodologies(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-guided" className="font-medium">Disable Guided Methodologies</Label>
                    <p className="text-sm text-gray-500">Hides all guided methodology content</p>
                  </div>
                  <Switch 
                    id="disable-guided" 
                    checked={disableGuidedMethodologies}
                    onCheckedChange={(checked) => setDisableGuidedMethodologies(checked)}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-pathway" className="font-medium">Disable Pathway Methodology</Label>
                    <p className="text-sm text-gray-500">Hides all pathway methodology content</p>
                  </div>
                  <Switch 
                    id="disable-pathway" 
                    checked={disablePathway}
                    onCheckedChange={(checked) => setDisablePathway(checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-pentest-orchestrator" className="font-medium">Disable Pentest Orchestrator</Label>
                    <p className="text-sm text-gray-500">Turns off the Pentest Orchestrator for all users. When disabled, users see 'Under development, check again later.'</p>
                  </div>
                  <Switch
                    id="disable-pentest-orchestrator"
                    checked={disablePentestOrchestrator}
                    onCheckedChange={setDisablePentestOrchestrator}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-hunters-board" className="font-medium">Disable Hunter's Board</Label>
                    <p className="text-sm text-gray-500">Turns off the Hunter's Board for all users. When disabled, users see 'Under development, check again later.'</p>
                  </div>
                  <Switch
                    id="disable-hunters-board"
                    checked={disableHuntersBoard}
                    onCheckedChange={setDisableHuntersBoard}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="disable-hacker-checklists" className="font-medium">Disable Hacker Checklists</Label>
                    <p className="text-sm text-gray-500">Hides all hacker checklist playbooks from the dashboard.</p>
                  </div>
                  <Switch
                    id="disable-hacker-checklists"
                    checked={disableHackerChecklists}
                    onCheckedChange={setDisableHackerChecklists}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end mt-4">
              <Button 
                className="bg-[#daed6e] hover:bg-[#c5d85c] text-black font-medium px-6"
                onClick={(e) => {
                  e.preventDefault();
                  handleSaveSettings();
                }}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </Button>
            </div>

            <Card className="border-dashed border-gray-300 bg-gray-50">
              <CardHeader>
                <CardTitle>Coming Soon</CardTitle>
                <CardDescription>More configuration options will be available in future updates</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="list-disc pl-5 space-y-2 text-gray-500">
                  <li>User role management</li>
                  <li>Content moderation settings</li>
                  <li>Custom branding options</li>
                  <li>API access controls</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}; 